import React, {useContext, useEffect, useState} from 'react';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import CloseIcon from '@mui/icons-material/Close';

import {videoApi} from '../../api/openapi-axios-client';
import {Loading, Modal} from '../../elements';
import VideoPlayer from './VideoPlayer';
import RootContext from '../../services/context-states/root-context';
import {truncateString} from '../../helpers/strings';
import { GetHistory200Response, RewatchVideoData, VideoHistoryPagination, VideoWatchLogWithONet } from '../../types';
import { callApi } from '../../api/helpers';

const DEFAULT_PAGINATION: VideoHistoryPagination = {
    pageCount: 1,
    pageNumber: 1,
    totalCount: 0
};

const VideoHistoryPage: React.FC = () => {
    const {userContext} = useContext(RootContext);
    const {currentUser} = userContext;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [videoHistory, setVideoHistory] = useState<VideoWatchLogWithONet[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [displayRewatchModal, toggleRewatchModal] = useState<boolean>(false);
    const [rewatchVideoData, setRewatchVideoData] = useState<RewatchVideoData | null>(null);
    const [pagination, updatePagination] = useState<VideoHistoryPagination>(DEFAULT_PAGINATION);

    useEffect(() => {
        fetchVideoHistory(pagination.pageNumber);
    }, [currentUser]);

    const fetchVideoHistory = async (page: number): Promise<void> => {
        if (currentUser?.id) {
            setIsLoading(true);
            try {
                const data = await callApi<GetHistory200Response>(() => videoApi.watchLog.getHistory('paged', currentUser.id, page, 10, 'yes'));
                const {results, ...paginationData} = data;
                setVideoHistory(videoHistory.concat(results));
                updatePagination(paginationData);
            } catch (error) {
                // Handle error silently
            } finally {
                setIsLoading(false);
            }
        }
    };

    const rewatchVideo = (video: VideoWatchLogWithONet): void => {
        setRewatchVideoData({
            id: video.sourceId,
            watchId: video.watchId
        });
        toggleRewatchModal(true);
    };

    return (
        <>
        <Modal
            open={displayRewatchModal}
            onClose={() => toggleRewatchModal(false)}
            modalTitle='Watch Video'
            modalDescription='Watch Video'
            style={{
                width: isMobile ? '95%' : isTablet ? '80%' : '60%',
                maxWidth: '800px'
            }}>
                <Stack spacing={2}>
                    <IconButton
                        aria-label="close"
                        onClick={() => toggleRewatchModal(false)}
                        sx={{position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500]}}>
                        <CloseIcon />
                    </IconButton>
                    {rewatchVideoData?.id && <VideoPlayer
                        video={rewatchVideoData}
                        autoplay={1}
                        height={isMobile ? '300' : isTablet ? '400' : '500'}
                        width={isMobile ? '100%' : isTablet ? '100%' : '100%'}
                        addtionalOptions={{}}
                        additionalVars={{}}
                        onEndFunc={() => {}}
                    />}
                </Stack>
        </Modal>
        {(isLoading && <Loading />) ||
            <Container
                maxWidth={isMobile ? false : "lg"}
                disableGutters={isMobile}
                sx={{
                    py: { xs: 2, sm: 3, md: 4 },
                    px: { xs: 2, sm: 3, md: 3 },
                    width: '100%'
                }}>
                <Typography sx={{
                    fontWeight: 900,
                    fontSize: { xs: 20, sm: 24, md: 28 },
                    mb: 1
                }}>
                    Watch History
                </Typography>
                <Typography sx={{
                    fontSize: { xs: 16, sm: 18, md: 20 },
                    mb: { xs: 2, sm: 3, md: 4 },
                    color: 'text.secondary'
                }}>
                    Look at all the videos you've already watched
                </Typography>
                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                    {videoHistory.length > 0 && videoHistory.map(video => (
                        <Grid key={video.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                            <Card
                                sx={{
                                    height: '100%',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 4
                                    }
                                }}>
                                <CardActionArea onClick={() => rewatchVideo(video)}>
                                    <CardMedia
                                        component="img"
                                        height={isMobile ? 140 : 175}
                                        image={video.thumbnail}
                                        alt={`Thumbnail for ${video.occupationTitle || 'video'}`}
                                        sx={{ objectFit: 'cover' }}
                                    />
                                    <CardContent>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 14, sm: 15, md: 16 },
                                                fontWeight: 'bold',
                                                color: 'text.primary',
                                                lineHeight: 1.3
                                            }}>
                                            {truncateString(video.videoTitle, isMobile ? 25 : 30)}
                                        </Typography>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
                {pagination && pagination.pageCount > pagination.pageNumber && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 3, sm: 4 } }}>
                        <Button
                            variant="contained"
                            size={isMobile ? 'medium' : 'large'}
                            onClick={() => fetchVideoHistory(pagination.pageNumber + 1)}
                            sx={{ minWidth: { xs: 120, sm: 150 } }}
                        >
                            See More
                        </Button>
                    </Box>
                )}
            </Container>
        }
        </>
    );
};

export default VideoHistoryPage; 